import {
  cloneElement,
  createContext,
  useContext,
  useId,
  type ReactElement,
  type ReactNode,
} from "react";
import { Download, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import { Select } from "@/components/ui/select";
import {
  downloadCsv,
  filenameSlug,
  toCsv,
  type CsvValue,
} from "@/lib/utils/csv";
import { formatPortraitMonth, monthRange } from "./customer-portrait-model";

export const COLORS = [
  "var(--color-admin-primary)",
  "var(--color-admin-info)",
  "var(--color-admin-mint-400)",
  "var(--color-admin-warn-400)",
  "var(--color-admin-danger-400)",
  "var(--color-admin-text-subtle)",
];
export const number = (value: number) =>
  value.toLocaleString("nb-NO", { maximumFractionDigits: 1 });
export const percent = (value: number) => `${number(value)} %`;
export const signed = (value: number | null) =>
  value === null ? "—" : `${value > 0 ? "+" : ""}${number(value)}`;
export const PortraitExportContext = createContext({
  example: false,
  asOf: "",
});
export type PortraitBucket = {
  label: string;
  count: number | null;
  denominator: number | null;
  percent: number | null;
};

export function MonthSelect({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: string;
  onChange: (month: string) => void;
}) {
  const year = new Date().getUTCFullYear();
  const months = [
    ...new Set([...monthRange(`${year - 2}-01`, `${year + 1}-12`), value]),
  ].sort();
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {months.map((month) => (
        <option key={month} value={month}>
          {formatPortraitMonth(month)}
        </option>
      ))}
    </Select>
  );
}
export function Information({ text }: { text: string }) {
  return (
    <Hint text={text} decorate={false}>
      <span className="inline-flex align-middle" aria-label="Forklaring">
        <Info className="h-3.5 w-3.5 text-admin-text-subtle" />
      </span>
    </Hint>
  );
}
export function Field({
  label,
  info,
  children,
}: {
  label: string;
  info?: string;
  children: ReactElement<{ id?: string }>;
}) {
  const id = useId();
  return (
    <div className="min-w-36 space-y-1">
      <label htmlFor={id} className="block text-xs text-admin-text-subtle">
        {label} {info && <Information text={info} />}
      </label>
      {cloneElement(children, { id })}
    </div>
  );
}
export function Export({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: CsvValue[][];
}) {
  const { example, asOf } = useContext(PortraitExportContext);
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!rows.length}
      onClick={() => {
        downloadCsv(
          toCsv(headers, rows, ";", { neutralizeFormulas: true }),
          `${example ? "eksempel-" : ""}kundebilde-${filenameSlug(title)}-${asOf}.csv`,
          { bom: true },
        );
      }}
    >
      <Download className="mr-1 h-3 w-3" />
      CSV
    </Button>
  );
}
export function Panel({
  title,
  info,
  subtitle,
  action,
  children,
}: {
  title: string;
  info: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              {title}
              <Information text={info} />
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-admin-text-subtle">{subtitle}</p>
            )}
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
export function Distribution({
  title,
  info,
  rows,
  subtitle,
}: {
  title: string;
  info: string;
  rows: PortraitBucket[];
  subtitle?: string;
}) {
  return (
    <Panel
      title={title}
      info={info}
      subtitle={subtitle}
      action={
        <Export
          title={title}
          headers={["Alternativ", "Antall", "Grunnlag", "Andel (%)"]}
          rows={rows.map((r) => [
            r.label,
            r.count,
            r.denominator,
            r.percent === null ? null : number(r.percent),
          ])}
        />
      }
    >
      {!rows.length && (
        <p className="text-sm text-admin-text-subtle">
          Ingen publiserbare verdier. Helsedata kan være deaktivert, skjermet
          eller mangle.
        </p>
      )}
      <ul className="space-y-3">
        {rows.map((row, i) => (
          <li key={row.label}>
            <div className="mb-1 flex justify-between gap-4 text-sm">
              <span className="text-admin-text-secondary">{row.label}</span>
              <span className="shrink-0 tabular-nums">
                {row.denominator && row.percent !== null
                  ? percent(row.percent)
                  : "—"}
                <span className="ml-2 text-xs text-admin-text-subtle">
                  {row.count === null ? "Skjermet" : `n=${row.count}`}
                </span>
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-admin-card-header">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${row.percent ?? 0}%`,
                  backgroundColor: COLORS[i % COLORS.length],
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
export function Stat({
  title,
  value,
  note,
  info,
  icon,
}: {
  title: string;
  value: string;
  note: string;
  info: string;
  icon: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between text-xs text-admin-text-subtle">
          <span className="flex items-center gap-2">
            {title}
            <Information text={info} />
          </span>
          {icon}
        </div>
        <p className="mt-2 text-3xl font-semibold tabular-nums">{value}</p>
        <p className="mt-1 text-xs text-admin-text-subtle">{note}</p>
      </CardContent>
    </Card>
  );
}
